import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  ReportFieldMetadata,
  ReportConfig,
  ChartReportResponse,
  PivotReportResponse,
  AssetReportView,
} from 'shared/types/asset-report';

// ─── Field Metadata ─────────────────────────────────────────────────

interface UseReportFieldMetadataReturn {
  fieldMetadata: ReportFieldMetadata[];
  isLoading: boolean;
  error: string | null;
}

export const useReportFieldMetadata = (categoryId?: number): UseReportFieldMetadataReturn => {
  const { session } = useSession();
  const [fieldMetadata, setFieldMetadata] = useState<ReportFieldMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;

    const fetchMetadata = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = categoryId
          ? `${API_URL}/assets/reports/field-metadata?categoryId=${categoryId}`
          : `${API_URL}/assets/reports/field-metadata`;
        const response = await fetchWithAuth(url, { method: 'GET' }, session);
        if (!response.ok) throw new Error('Fout bij ophalen veldmetadata');
        const data = await response.json();
        setFieldMetadata(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [session, categoryId]);

  return { fieldMetadata, isLoading, error };
};

// ─── Execute Report Query ───────────────────────────────────────────

interface UseExecuteReportReturn {
  data: ChartReportResponse | PivotReportResponse | null;
  isLoading: boolean;
  error: string | null;
  execute: (config: ReportConfig) => Promise<void>;
}

export const useExecuteReport = (): UseExecuteReportReturn => {
  const { session } = useSession();
  const [data, setData] = useState<ChartReportResponse | PivotReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (config: ReportConfig) => {
    if (!session) return;
    if (!config.measures.length) return;

    setIsLoading(true);
    setError(null);
    setData(null); // Clear stale data immediately
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/reports/query`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            viewType: config.viewType,
            rowDimensions: config.rowDimensions,
            columnDimensions: config.columnDimensions,
            measures: config.measures,
            filterCriteria: config.filterCriteria,
          }),
        },
        session,
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Fout bij uitvoeren rapportage');
      }
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  return { data, isLoading, error, execute };
};

// ─── Saved Views ────────────────────────────────────────────────────

interface UseSavedViewsReturn {
  views: AssetReportView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  saveView: (view: Omit<AssetReportView, 'id' | 'createdByClerkId' | 'createdAt' | 'updatedAt'>) => Promise<AssetReportView | null>;
  updateView: (id: number, view: Partial<AssetReportView>) => Promise<AssetReportView | null>;
  deleteView: (id: number) => Promise<boolean>;
}

export const useSavedViews = (): UseSavedViewsReturn => {
  const { session } = useSession();
  const [views, setViews] = useState<AssetReportView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchViews = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/assets/reports/views`, { method: 'GET' }, session);
      if (!response.ok) throw new Error('Fout bij ophalen opgeslagen weergaven');
      const data = await response.json();
      setViews(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  const saveView = useCallback(async (
    view: Omit<AssetReportView, 'id' | 'createdByClerkId' | 'createdAt' | 'updatedAt'>,
  ): Promise<AssetReportView | null> => {
    if (!session) return null;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/reports/views`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(view),
        },
        session,
      );
      if (!response.ok) throw new Error('Fout bij opslaan weergave');
      const saved = await response.json();
      setViews(prev => [saved, ...prev]);
      return saved;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  }, [session]);

  const updateView = useCallback(async (
    id: number,
    view: Partial<AssetReportView>,
  ): Promise<AssetReportView | null> => {
    if (!session) return null;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/reports/views/${id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(view),
        },
        session,
      );
      if (!response.ok) throw new Error('Fout bij bijwerken weergave');
      const updated = await response.json();
      setViews(prev => prev.map(v => v.id === id ? updated : v));
      return updated;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  }, [session]);

  const deleteView = useCallback(async (id: number): Promise<boolean> => {
    if (!session) return false;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/reports/views/${id}`,
        { method: 'DELETE' },
        session,
      );
      if (!response.ok) throw new Error('Fout bij verwijderen weergave');
      setViews(prev => prev.filter(v => v.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  }, [session]);

  return { views, isLoading, error, refetch: fetchViews, saveView, updateView, deleteView };
};
